/* eslint-disable no-param-reassign */
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { ValuationApproachDCF } from 'api';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { LONG_TERM_GROWTH_RATE, TERMINAL_VALUE, TERMINAL_VALUE_OPTIONS } from 'pages/Valuations/util/constants';
import DCFApproachContext from 'pages/ValuationsAllocation/approaches/DiscountCashFlow/context/DCFApproachContext';
import { ValuationSpreadsheets } from 'pages/ValuationsAllocation/hooks/useCreateValuation/types';
import useTableValidation from 'services/hooks/useTableValidation';
import { getObjectValue, getStringValue } from 'utilities';
import { DCFTerminalValueTableProps } from './types';

const tvtSpreadSheetKey = {
  [TERMINAL_VALUE_OPTIONS.PERPETUITY_GROWTH]: 'dcfTVTPG',
  [TERMINAL_VALUE_OPTIONS.H_MODEL]: 'dcfTVTHM',
  [TERMINAL_VALUE_OPTIONS.REVENUE_MULTIPLE]: 'dcfTVTRM',
  [TERMINAL_VALUE_OPTIONS.EBITDA_MULTIPLE]: 'dcfTVTEM',
  [TERMINAL_VALUE_OPTIONS.REVENUE_AND_EBITDA_MULTIPLE]: 'dcfTVTREM',
};

const DCFTerminalValueTable = (props: DCFTerminalValueTableProps) => {
  const { format, formatDispatch, spreadsheets, onChange: onWorkbookChange, workbook, terminalValue, approach } = props;
  const dcfTvtKey = tvtSpreadSheetKey[terminalValue ?? TERMINAL_VALUE_OPTIONS.PERPETUITY_GROWTH];
  // Saved inputs can rebuild the workbook; never render a cached outgoing sheet.
  const dcfTVTProps = spreadsheets[dcfTvtKey as ValuationSpreadsheets];
  const { validateCells } = useTableValidation();

  const { valuations_approach_dcf } = getObjectValue(approach);
  const { setCurrentTerminalValue } = useContext(DCFApproachContext);

  const changeTvtType = useCallback(
    ({ tvtType }) => {
      const newSpreadsheetConfigs = spreadsheets[tvtSpreadSheetKey[tvtType] as ValuationSpreadsheets];
      const spreadsheetsKeys = Object.values(tvtSpreadSheetKey) as ValuationSpreadsheets[];
      spreadsheetsKeys.forEach(key => {
        const spreadsheetsColumns = (spreadsheets[key].columns as ValuationApproachDCF[])[0];
        spreadsheetsColumns.terminal_value = tvtType;
      });
      setCurrentTerminalValue(newSpreadsheetConfigs);
    },
    [setCurrentTerminalValue, spreadsheets]
  );

  // Legacy copies retain their inputs; show invalid saved growth on open, before the next Save.
  useEffect(() => {
    if (!dcfTVTProps?.cells) {
      return;
    }
    const growthCell = Object.values(dcfTVTProps.cells).find(cell => cell.alias === LONG_TERM_GROWTH_RATE);
    if (!growthCell?.sheet?.customValidations) {
      return;
    }
    validateCells({
      cellsToValidate: [growthCell],
      parsedColumns: dcfTVTProps.cells,
      tableData: dcfTVTProps.tableData,
      customValidations: growthCell.sheet.customValidations,
      useOriginalCell: true,
    });
    growthCell.dispatchEvent?.();
  }, [dcfTVTProps, validateCells, workbook]);

  const onChange = useCallback(
    async (cell, expr) => {
      if (!onWorkbookChange) {
        return;
      }
      await onWorkbookChange(cell, expr);

      // A rejected edit must not switch the visible method or overwrite its model through the sheet columns.
      if (cell.alias === TERMINAL_VALUE && valuations_approach_dcf?.terminal_value === expr) {
        changeTvtType({ tvtType: expr });
      }
    },
    [changeTvtType, onWorkbookChange, valuations_approach_dcf]
  );

  const dCFTerminalValueTableSheetProps = useMemo(
    () =>
      ({
        ...dcfTVTProps,
        sheet: dcfTVTProps,
        key: getStringValue(dcfTVTProps?.name),
        workbook,
        onChange,
        format,
        formatDispatch,
      }) as unknown as typeof ScalarSpreadsheet,
    [dcfTVTProps, workbook, onChange, format, formatDispatch]
  );

  return (
    <div>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore: ScalarSpreadsheet is not typed */}
      <ScalarSpreadsheet {...dCFTerminalValueTableSheetProps} />
    </div>
  );
};

export default DCFTerminalValueTable;
